// @ts-nocheck - verbatim port from my-location-next/utils (plain JS, not authored to TS strict). Type it if/when reworked.
// Reusable relative-time formatter. Used by:
//   - FriendMarker popup ("Updated 4 min ago")
//   - SharedMap snapshot meta row ("Shared 2 h ago")
//   - (future) FriendsList row staleness, route detail, etc.
//
// Thresholds match SyncChip.formatSynced for consistency across the app.
// All strings are localised via the active i18n catalog; verb-baked keys
// (time.{verb}.justNow, .minsAgo, etc.) keep translations honest in
// languages where naïve "{verb} {ago}" concatenation breaks word order.

const SEC = 1000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const MONTH_30 = 30 * DAY;

/**
 * Returns a fully-localised "Updated 4 min ago" style string.
 *
 *   t(key, vars)  — translate function from useTranslate()
 *   tsMs          — number (ms epoch) or ISO 8601 string; null/undefined OK
 *   verb          — 'updated' | 'shared'   (lookup key prefix; see strings/en.js)
 *
 * Falls back to an absolute date once the timestamp is more than 30 days old.
 * Returns an empty string for null/invalid input.
 */
export function formatRelative(t, tsMs, { verb = 'updated' } = {}) {
  const ms = normalize(tsMs);
  if (ms == null) return '';
  const ageMs = Math.max(0, Date.now() - ms);

  if (ageMs < 5 * SEC) return t(`time.${verb}.justNow`);
  if (ageMs < MIN) {
    const secs = Math.max(1, Math.round(ageMs / SEC));
    return t(`time.${verb}.secsAgo`, { secs });
  }
  if (ageMs < HOUR) {
    const mins = Math.max(1, Math.round(ageMs / MIN));
    return t(`time.${verb}.minsAgo`, { mins });
  }
  if (ageMs < DAY) {
    const hours = Math.max(1, Math.round(ageMs / HOUR));
    return t(`time.${verb}.hoursAgo`, { hours });
  }
  if (ageMs < MONTH_30) {
    const days = Math.max(1, Math.round(ageMs / DAY));
    return t(`time.${verb}.daysAgo`, { days });
  }
  // > 30 days: localised absolute date ("Shared on Jan 14")
  const date = new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return t(`time.${verb}.absolute`, { date });
}

function normalize(input) {
  if (input == null) return null;
  if (typeof input === 'number' && Number.isFinite(input)) return input;
  if (typeof input === 'string') {
    const parsed = Date.parse(input);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (input instanceof Date) return input.getTime();
  return null;
}

/** Clock time without seconds ("18:42" or "6:42 PM", by locale). '' for bad input. */
export function formatClock(tsMs, locale) {
  const ms = normalize(tsMs);
  if (ms == null) return '';
  try {
    return new Date(ms).toLocaleTimeString(locale || undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
}

/** Date and clock time without seconds ("Sep 26, 14:32"). Today shows the time only. */
export function formatDateTime(tsMs, locale) {
  const ms = normalize(tsMs);
  if (ms == null) return '';
  const d = new Date(ms);
  if (d.toDateString() === new Date().toDateString()) return formatClock(ms, locale);
  try {
    return d.toLocaleString(locale || undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
}

/** The one format for GPS accuracy: "±12 m", and "±1.2 km" from 1000 m up. */
export function formatAccuracy(meters) {
  const m = Number(meters);
  if (!Number.isFinite(m) || m <= 0) return '';
  if (m < 1000) return `±${Math.max(1, Math.round(m))} m`;
  const km = m / 1000;
  return `±${km < 10 ? Math.round(km * 10) / 10 : Math.round(km)} km`;
}

/** Whole minutes left, never below 1 while any time remains. */
export function minutesLeft(ms) {
  return ms > 0 ? Math.max(1, Math.ceil(ms / 60000)) : 0;
}
